import React, { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Grid } from 'basis';
import { useSteps } from 'hooks';
import { isFeatureOn } from 'featureToggles';
import { useEventTracking } from 'react-event-tracker';
import { startCase } from 'lodash';
import { byCountry } from 'utils';
import styled from '@emotion/styled';
import { SaveAndCompleteLaterButton } from 'components/save-and-resume';
import { LayoutContent } from 'components/layout-content/LayoutContent';

export const FormButtons = ({
  formId,
  formData,
  loading,
  disabled,
  submitButtonLabel,
  noSaveDraft,
  showBackButton,
}) => {
  const history = useHistory();
  const { activeStep, pathname, isDrafting, previousStep, accessibleSteps, nextStep } = useSteps();
  const hasBackButton =
    showBackButton ?? (isDrafting && activeStep !== accessibleSteps[0] && pathname !== accessibleSteps[0]);

  const { trackEvent } = useEventTracking();
  const onClickBack = useCallback(() => {
    trackEvent({
      event: {
        category: 'application',
        action: 'application-navigation',
        location: pathname.slice(1),
        label: 'Back',
      },
    });
    if (previousStep) {
      history.push(previousStep);
    }
  }, [history, previousStep, trackEvent, pathname]);

  const buttonLabel = useMemo(() => {
    const nextStepTitle = startCase(nextStep);
    return submitButtonLabel.replace(/%%NEXT_STEP/, nextStepTitle);
  }, [nextStep, submitButtonLabel]);

  const isSaveAndResumeFeatureOn = byCountry({
    AU: isFeatureOn('saveAndResume'),
    NZ: isFeatureOn('saveAndResume') && isFeatureOn('showSaveAndResumeButton'),
  });

  const formButtonsConfig = {
    hasBackButton,
    buttonLabel,
    loading,
    disabled,
    onClickBack,
    formId,
    formData,
    isDrafting,
    noSaveDraft,
    isSaveAndResumeFeatureOn,
  };

  const ButtonGroup = ButtonGroupWithSaveAndCompleteLaterButton;

  return (
    <LayoutContent>
      <ButtonGroup {...formButtonsConfig} />
    </LayoutContent>
  );
};

const ButtonGroupWithSaveAndCompleteLaterButton = ({
  hasBackButton,
  buttonLabel,
  loading,
  disabled,
  onClickBack,
  formId,
  formData,
  isDrafting,
  noSaveDraft,
  isSaveAndResumeFeatureOn,
}) => {
  return (
    <Grid rowsGap="4" colsGap="10" cols="2">
      <Grid.Item colSpan="all" colSpan-sm="0" alignItems="center">
        <Grid>
          <Button type="submit" loading={loading} disabled={disabled} padding="0 2">
            {buttonLabel}
          </Button>
        </Grid>
      </Grid.Item>
      {isSaveAndResumeFeatureOn && isDrafting && noSaveDraft !== true && (
        <Grid.Item colSpan="all" colSpan-sm="1" rowSpan-sm="0">
          <Grid>
            <SaveAndCompleteLaterButton formId={formId} formData={formData} isDrafting />
          </Grid>
        </Grid.Item>
      )}
    </Grid>
  );
};

const FormButtonWrapper = styled.div`
  & button {
    padding: 12px 16px;
  }
`;

export const PrimaryButtonWrapper = styled(FormButtonWrapper)`
  && button {
    background-color: #20df5c;
    color: var(--Flat-colours-Indigo, var(--Indigo-1000, #0b2374));
    font-family: var(--Family-font-brand, 'Latitude Sans');
    font-weight: 600;

    &:hover:not(:disabled) {
      color: var(--Flat-colours-Indigo, var(--Indigo-1000, #0b2374));
      background-color: var(--Background-Primary-hover, #6cf898);
    }
  }
`;
